import DOMPurify from "dompurify";
import {BACKGROUNDS} from "../data/constants";
import {escapeRegExp, sortValuesByKey, isVideoSource} from "./utils";

/**
 * Shared `select` transform for the `get-backgrounds` React Query — used by
 * both `useBackgrounds` and `useBgData` so they produce identical data.
 * Lives here so the full-featured hook doesn't import from the narrow one.
 * @param {{tile_sets?: unknown}} raw
 * @returns {unknown[]}
 */
export const selectBackgrounds = (raw) => sortValuesByKey(raw.tile_sets);

// Background style markers (blacklogo, showgrid, …) live in libs/bgMarkers.ts —
// the catalog persists them per image, so they can't be a client-only concern.

// Background URLs from the tiles endpoint carry a `?v=<etag>` cache-busting
// suffix so a replaced image bypasses the browser/CDN cache. Match the .png
// extension whether it sits at the end or is followed by that query string —
// a bare `url.endsWith(".png")` would reject every cache-busted URL.
export const isPngUrl = (url) => /\.png(\?|$)/i.test(url);

export const findBgUrl = (arr, index, partialSubstring) => {
  if (!arr[index - 1]) return undefined;

  const productValueMatch = partialSubstring.match(/\/([^/]+)$/);
  let pattern;

  if (productValueMatch) {
    const matchValue = productValueMatch[1];

    if (!matchValue.includes("@") && !matchValue.includes("-")) {
      // Require a hard boundary after product code to avoid prefix matches (e.g., JB vs JBD)
      // Accept only '_' or '.' as the next character after the product code
      const escaped = escapeRegExp(partialSubstring);
      pattern = new RegExp(`${escaped}(?=[_.])`, "i");
    } else {
      const escaped = escapeRegExp(partialSubstring);
      pattern = new RegExp(escaped, "i");
    }
  } else {
    const escaped = escapeRegExp(partialSubstring);
    pattern = new RegExp(escaped, "i");
  }

  return arr[index - 1].find((url) => pattern.test(url) && isPngUrl(url));
};

export const getHashes = (arraysOfArrays, productValue = null) => {
  const hashes = {};
  const defaultHashes = [];

  arraysOfArrays.forEach((subArray, outerIndex) => {
    const defaultHashUrl = subArray.find((str) => str.includes("/#")) || "";
    const defaultHash = extractHash(defaultHashUrl, "#");
    if (defaultHash) {
      defaultHashes[outerIndex] = defaultHash;
    }

    if (productValue) {
      const selectedUrl =
        findBgUrl(arraysOfArrays, outerIndex + 1, `/${productValue}`) ||
        (productValue === "ALL"
          ? null
          : findBgUrl(arraysOfArrays, outerIndex + 1, `/${productValue}-outdated`));

      if (selectedUrl?.includes("_&")) {
        if (!hashes[productValue]) {
          hashes[productValue] = [];
        }
        hashes[productValue][outerIndex] = extractHash(selectedUrl, "&");
      }
      return;
    }

    subArray
      .filter((str) => str.includes("_&"))
      .forEach((str) => {
        const key = extractProduct(str, outerIndex + 1);
        const value = extractHash(str, "&");

        if (!hashes[key]) {
          hashes[key] = [];
        }
        hashes[key][outerIndex] = value;
      });
  });
  hashes["default"] = defaultHashes;

  return Object.keys(hashes).length ? hashes : {};
};

function extractProduct(string, index) {
  const startStr = `${index}/`;
  const startIndexRaw = string.indexOf(startStr);
  if (startIndexRaw === -1) return "";
  const startIndex = startIndexRaw + startStr.length;
  const underscoreIndex = string.indexOf("_", startIndex);
  const dotIndex = string.indexOf(".", startIndex);
  let endIndex;
  if (underscoreIndex === -1 && dotIndex === -1) {
    endIndex = string.length;
  } else if (underscoreIndex === -1) {
    endIndex = dotIndex;
  } else if (dotIndex === -1) {
    endIndex = underscoreIndex;
  } else {
    endIndex = Math.min(underscoreIndex, dotIndex);
  }
  return string.substring(startIndex, endIndex);
}

function extractHash(string, symbol) {
  const hashIndex = string.indexOf(symbol);
  const dotIndex = string.indexOf(".", hashIndex);
  return string.substring(hashIndex + 1, dotIndex) || null;
}

export const getBgUrl = (channel, type) => {
  if (type) {
    return `${BACKGROUNDS}/${channel}/${type}`;
  }
  return `${BACKGROUNDS}/${channel}`;
};

// getLogoUrl and getLogoCycle moved to libs/logo.js (re-exported above)

export const getThumbnailUrl = (inputString) => {
  const TARGET_FOLDER = "tiles";
  const REPLACEMENT_FOLDER = "gdrive_thumbnails";
  // Replace `.png` whether it ends the URL or is followed by a `?v=` cache-bust
  // query (the lookahead keeps any query string intact: foo.png?v=x → foo.webp?v=x).
  const TARGET_EXTENSION = /\.png(?=\?|$)/;
  const REPLACEMENT_EXTENSION = ".webp";

  return inputString
    .replace(TARGET_FOLDER, REPLACEMENT_FOLDER)
    .replace(TARGET_EXTENSION, REPLACEMENT_EXTENSION);
};

/**
 * True for SVG files — detected by MIME type or `.svg` extension (some
 * browsers report an empty `type` for `.svg`). SVG uploads take the inline
 * data-URL path rather than going to S3 via /api/v3/uploads: the upload
 * allowlist deliberately rejects `image/svg+xml` (it can carry script), and
 * SVG renders fine through `<img src>` / CSS background (sandboxed mode — no
 * scripts, no external loads). Shared by BgSelector, ImageUpload, and the
 * Toolbar QR uploader so all three classify SVG identically.
 */
export function isSvgFile(file) {
  return file?.type === "image/svg+xml" || /\.svg$/i.test(file?.name || "");
}

/** Read a File into a base64 data URL. */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("FileReader returned a non-string result"));
    };
    reader.onerror = () => reject(reader.error || new Error("FileReader error"));
    reader.readAsDataURL(file);
  });
}

export async function checkIfAnimated(input) {
  if (!input) return false;
  try {
    let file;
    // If string (URL), then load the file
    if (typeof input === "string") {
      const response = await fetch(input);
      const blob = await response.blob();
      file = blob;
    } else {
      // If File or Blob
      file = input;
    }

    // Check if it's a video file by MIME type
    if (file.type && file.type.startsWith("video/")) {
      return true;
    }

    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // Check SVG
    const svgSignature = new TextDecoder().decode(bytes.slice(0, 100));
    if (svgSignature.includes("<svg") || svgSignature.includes("<?xml")) {
      const text = new TextDecoder().decode(bytes);

      // Check animation tags
      const hasAnimateTag = /<animate[^>]*>/i.test(text);
      const hasAnimateTransform = /<animateTransform[^>]*>/i.test(text);
      const hasAnimateMotion = /<animateMotion[^>]*>/i.test(text);
      const hasAnimationInStyle = /@keyframes|animation:/i.test(text);

      return hasAnimateTag || hasAnimateTransform || hasAnimateMotion || hasAnimationInStyle;
    }

    // Check GIF
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
      // GIF signature
      for (let i = 0; i < bytes.length - 3; i++) {
        if (bytes[i] === 0x21 && bytes[i + 1] === 0xf9) {
          // Graphics Control Extension
          return true;
        }
      }
      return false;
    }

    // Check APNG
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
      // PNG signature
      for (let i = 0; i < bytes.length - 4; i++) {
        if (
          bytes[i] === 0x61 &&
          bytes[i + 1] === 0x63 &&
          bytes[i + 2] === 0x54 &&
          bytes[i + 3] === 0x4c
        ) {
          // acTL chunk (animated PNG)
          return true;
        }
      }
      return false;
    }

    // WebP
    if (
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      // RIFF...WEBP
      for (let i = 12; i < bytes.length - 4; i++) {
        if (
          bytes[i] === 0x41 &&
          bytes[i + 1] === 0x4e &&
          bytes[i + 2] === 0x49 &&
          bytes[i + 3] === 0x4d
        ) {
          // ANIM chunk
          return true;
        }
      }
      return false;
    }

    return false;
  } catch (error) {
    console.error("Error checking animation:", error);
    return false;
  }
}

/**
 * If `src` is an SVG data URL (or a raw `<svg>` string), decode it to SVG
 * markup and run it through DOMPurify's SVG allowlist before it is handed
 * to a caller that inlines it via innerHTML.
 *
 * Returns null for anything that isn't recognizable SVG, including decode
 * failures and payloads DOMPurify strips down to nothing — callers should
 * treat null as "render via <img> instead, don't inline."
 *
 * Why an allowlist: once SVG markup is inlined via innerHTML, event
 * handlers fire and `javascript:` hrefs are live (unlike `<img src=...>`,
 * which renders SVG in a sandboxed mode). Saved scenes round-trip this
 * data URL between users, so an attacker-uploaded SVG would otherwise run
 * code in a recipient's session. A hand-rolled denylist (strip <script>,
 * on*, javascript:) misses documented bypasses — SMIL `<animate>`/`<set>`
 * rewriting an href to `javascript:` at runtime, and mutation-XSS from the
 * parse→serialize→re-parse round-trip. DOMPurify's allowlist closes those
 * while preserving every element/attribute the gradient editor reads
 * (linear/radialGradient, stop, gradientTransform, x1/y1/.., fx/fy/.., the
 * id ↔ url(#id) wiring, etc.).
 */
export function decodeSvgFromDataUrl(src) {
  if (typeof src !== "string" || !src) return null;

  let markup = null;
  const trimmed = src.trim();
  if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) {
    markup = trimmed;
  } else if (trimmed.startsWith("data:image/svg")) {
    const commaIdx = trimmed.indexOf(",");
    if (commaIdx === -1) return null;
    const meta = trimmed.slice(5, commaIdx);
    const payload = trimmed.slice(commaIdx + 1);
    try {
      // base64 SVG is almost always UTF-8; atob() yields a Latin-1 byte
      // string, so decode the bytes back through TextDecoder or non-ASCII
      // text (Cyrillic, em-dashes) turns to mojibake. Mirrors the
      // TextEncoder write-back in GradientControls' utf8ToBase64.
      markup = meta.includes(";base64")
        ? new TextDecoder().decode(Uint8Array.from(atob(payload), (c) => c.charCodeAt(0)))
        : decodeURIComponent(payload);
    } catch (err) {
      console.warn("[decodeSvgFromDataUrl] decode failed:", err);
      return null;
    }
  } else {
    return null;
  }

  if (!markup || typeof markup !== "string") return null;

  const clean = DOMPurify.sanitize(markup, {
    USE_PROFILES: {svg: true, svgFilters: true},
  });

  // DOMPurify collapses a non-SVG or unsalvageable payload to "" or bare
  // text; with no surviving <svg> root there's nothing to inline.
  if (!clean || !/<svg[\s>]/i.test(clean)) return null;

  return clean;
}

/** True for a remote (http/https) URL that points at an .svg file. */
export function isSvgUrl(src) {
  return typeof src === "string" && /^https?:\/\//i.test(src) && /\.svg(\?|#|$)/i.test(src);
}

/**
 * Fetch a remote .svg URL and return it as a sanitized inline
 * `data:image/svg+xml;base64,…` URL — the form BiggerPicture inlines and the
 * GradientControls panel can read/edit. This is how a backend-stored SVG
 * background becomes gradient-editable: served from the CDN (with CORS), then
 * fetched + inlined on the client (and re-sanitized via decodeSvgFromDataUrl,
 * defense-in-depth on top of the server-side sanitize).
 *
 * Returns null on any failure (network/CORS error, non-SVG, sanitized to
 * nothing) so callers can fall back to a bundled data URL.
 */
export async function inlineSvgUrl(src) {
  if (!isSvgUrl(src)) return null;
  try {
    const res = await fetch(src, {mode: "cors"});
    if (!res.ok) return null;
    const markup = await res.text();
    const clean = decodeSvgFromDataUrl(markup);
    if (!clean) return null;
    const bytes = new TextEncoder().encode(clean);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return `data:image/svg+xml;base64,${btoa(binary)}`;
  } catch (err) {
    console.warn("[inlineSvgUrl] fetch/inline failed:", err);
    return null;
  }
}

export async function getSizeFromBase64(src) {
  return new Promise((resolve, reject) => {
    const isSvg = src.includes("data:image/svg");
    const isVideo = isVideoSource(src);

    if (isVideo) {
      // For video, create video element to get dimensions
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true; // Important for autoplay policies
      video.playsInline = true;
      video.crossOrigin = "anonymous";

      let hasResolved = false;

      const cleanup = () => {
        if (!hasResolved) {
          hasResolved = true;
          video.removeEventListener("loadedmetadata", onLoadedMetadata);
          video.removeEventListener("error", onError);
          video.removeEventListener("loadeddata", onLoadedData);
          video.src = "";
          video.load();
        }
      };

      function onLoadedMetadata() {
        const width = video.videoWidth;
        const height = video.videoHeight;

        // console.log("Video dimensions from loadedmetadata:", {width, height});

        if (width && height) {
          cleanup();
          resolve({width, height});
        }
      }

      function onLoadedData() {
        // Fallback: try to get dimensions from loadeddata event
        if (!hasResolved) {
          const width = video.videoWidth;
          const height = video.videoHeight;

          console.log("Video dimensions from loadeddata:", {width, height});

          if (width && height) {
            cleanup();
            resolve({width, height});
          }
        }
      }

      function onError(error) {
        console.error("Video loading error:", error);
        cleanup();
        // Fallback to common video dimensions
        resolve({width: 1920, height: 1080});
      }

      // Add event listeners
      video.addEventListener("loadedmetadata", onLoadedMetadata);
      video.addEventListener("loadeddata", onLoadedData);
      video.addEventListener("error", onError);

      // Set timeout as a last resort
      setTimeout(() => {
        if (!hasResolved) {
          console.warn("Video metadata loading timeout, using defaults");
          cleanup();
          resolve({width: 1920, height: 1080});
        }
      }, 5000); // 5 second timeout

      // Set src after attaching event listeners
      video.src = src;
    } else if (isSvg) {
      // For SVG, parse the content
      try {
        const base64Data = src.split(",")[1];
        const svgText = atob(base64Data);
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
        const svgElement = svgDoc.documentElement;

        // Get viewBox attribute
        const viewBox = svgElement.getAttribute("viewBox");
        if (viewBox) {
          const [, , width, height] = viewBox.split(/\s+/).map(Number);
          resolve({width, height});
          return;
        }

        // If no viewBox, get width and height attributes
        let width = svgElement.getAttribute("width");
        let height = svgElement.getAttribute("height");

        // Remove units (px, pt, etc.)
        if (width) width = parseFloat(width);
        if (height) height = parseFloat(height);

        // If dimensions exist, use them
        if (width && height) {
          resolve({width, height});
          return;
        }

        // Fallback: create img element in isolated container
        const img = new Image();
        img.onload = () => {
          // For SVG without explicit dimensions, browser usually renders 300x150 (per spec)
          // or uses container dimensions
          const w = img.naturalWidth || 300;
          const h = img.naturalHeight || 150;
          resolve({width: w, height: h});
        };
        img.onerror = () => resolve({width: 300, height: 150});
        img.src = src;
      } catch (error) {
        console.error("Error parsing SVG:", error);
        resolve({width: 300, height: 150});
      }
    } else {
      // For raster images, use Image element
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth || img.width,
          height: img.naturalHeight || img.height,
        });
      };
      img.onerror = reject;
      img.src = src;
    }
  });
}
